import { useState, useRef, useEffect, useCallback, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuth } from "../store/authStore";

const CATEGORIES = [
  { value: "improvement", label: "Improvement", icon: "\u{1F4A1}" },
  { value: "feature", label: "Feature", icon: "\u{1F680}" },
  { value: "bug", label: "Bug", icon: "\u{1F41B}" },
  { value: "praise", label: "Love it", icon: "\u2764\uFE0F" },
  { value: "general", label: "General", icon: "\u{1F4AC}" },
];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Drag state
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!panelRef.current) return;
    isDragging.current = true;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isDragging.current) return;
      const x = Math.max(0, Math.min(window.innerWidth - 420, e.clientX - dragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.current.y));
      setPosition({ x, y });
    }
    function handleMouseUp() {
      isDragging.current = false;
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Reset position when closing
  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  if (!user) return null;

  function reset() {
    setCategory("");
    setTitle("");
    setMessage("");
    setRating(0);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category, title, message, rating: rating || undefined,
      });
      reset();
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setOpen(false); }, 2000);
    } catch { /* failed */ }
    finally { setSubmitting(false); }
  }

  // Panel positioning: dragged position or default (above the coach widget bubble)
  const panelStyle: React.CSSProperties = position
    ? { left: position.x, top: position.y, right: "auto", bottom: "auto" }
    : {};

  return (
    <>
      {/* Floating button — stacked above the coach widget (bottom-6 right-6) */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-6 z-50 w-12 h-12 bg-slate-700 hover:bg-slate-800 text-white rounded-full shadow-lg shadow-slate-700/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        aria-label="Send feedback"
      >
        {open ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Feedback panel */}
      {open && (
        <div
          ref={panelRef}
          style={panelStyle}
          className={`fixed z-50 w-[420px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 border border-slate-200 flex flex-col overflow-hidden ${
            position ? "" : "bottom-40 right-6"
          }`}
        >
          {/* Header — drag handle */}
          <div
            onMouseDown={handleMouseDown}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3.5 flex items-center justify-between shrink-0 cursor-move select-none"
          >
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold text-sm">Send Feedback</h3>
              <span className="text-blue-200 text-[10px] hidden sm:inline">drag to move</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { navigate("/feedback"); setOpen(false); }}
                className="text-blue-100 hover:text-white text-xs underline underline-offset-2"
              >
                View history
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-blue-200 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {success ? (
            <div className="px-5 py-10 text-center">
              <div className="text-4xl mb-3">{"\u{1F389}"}</div>
              <p className="text-base font-medium text-slate-900">Thank you!</p>
              <p className="text-sm text-slate-500 mt-1">Your feedback helps us improve Madrich.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Category pills */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-2">What type of feedback?</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        category === cat.value
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief summary"
                required
                maxLength={200}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us more (at least 10 characters)..."
                required
                rows={5}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              />

              {/* Rating */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-600">Overall satisfaction:</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(rating === n ? 0 : n)}
                      className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${
                        rating >= n
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <span className="text-[10px] text-slate-400">optional</span>
              </div>

              <button
                type="submit"
                disabled={submitting || !category}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition-all"
              >
                {submitting ? "Sending..." : "Send Feedback"}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
